import type { InferResponseType } from 'hono/client'

import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/apiClient'

import { AttachmentsTab } from './attachments-tab'
import { DocumentDetails } from './document-details'
import { DocumentForm } from './document-form'
import { LogsTab } from './logs-tab'
import { adaptDocumentWithDetailsToDocument } from './type-adapter'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

interface MobileDocumentViewProps {
  document: DocumentWithDetails | undefined
  mode: 'view' | 'modify'
  tab: string
  onTabChange: (tab: string) => void
  previewContent: React.ReactNode
}

export function MobileDocumentView({
  document,
  mode,
  tab,
  onTabChange,
  previewContent,
}: MobileDocumentViewProps) {
  return (
    <div className="flex h-full grow flex-col overflow-hidden p-4">
      <Tabs
        className="flex h-full flex-col overflow-hidden"
        value={tab}
        onValueChange={onTabChange}
      >
        <div className="mb-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Dettagli</TabsTrigger>
            <TabsTrigger value="preview">Anteprima</TabsTrigger>
            <TabsTrigger value="attachments">
              Allegati
              {!!document?._count?.attachments &&
                ` (${document?._count?.attachments})`}
            </TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          className={cn(
            'grow overflow-y-auto p-0',
            tab === 'details' && 'flex flex-col'
          )}
          value="details"
        >
          <ScrollArea className="h-full">
            <div className="p-1">
              {mode === 'view' && document && (
                <DocumentDetails
                  document={adaptDocumentWithDetailsToDocument(document)}
                />
              )}
              {mode === 'modify' && document && (
                <DocumentForm
                  document={adaptDocumentWithDetailsToDocument(document)}
                />
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent
          className={cn(
            'grow overflow-y-auto p-0',
            tab === 'preview' && 'flex flex-col'
          )}
          value="preview"
        >
          {previewContent}
        </TabsContent>

        <TabsContent
          className={cn(
            'grow overflow-y-auto p-0',
            tab === 'attachments' && 'flex flex-col'
          )}
          value="attachments"
        >
          {document && (
            <AttachmentsTab
              document={adaptDocumentWithDetailsToDocument(document)}
            />
          )}
        </TabsContent>

        <TabsContent
          className={cn(
            'grow overflow-y-auto p-0',
            tab === 'logs' && 'flex flex-col'
          )}
          value="logs"
        >
          {document && (
            <LogsTab document={adaptDocumentWithDetailsToDocument(document)} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
